/**
 * AskUserForInfoRenderer — Renderer for `ask_user_for_info` proposals.
 *
 * Renders one labeled rich-text editor per `fields[]` entry. Autosaves
 * answers to the action payload via the `update_payload` rail and
 * integrates with the AutoSaveCoordination flush/collect rail so the
 * existing Approve button flushes pending edits before proceeding.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useEscapeToBlur } from '@/hooks/useEscapeToBlur';
import { Button } from '@/lib/base/buttons/button';
import { PlateNoteEditor } from '@/lib/crm/record/notes/PlateNoteEditor';
import { logger } from '@/lib/logger';

import { createProposalClient } from '../../../lib/proposals/core/ProposalClient';
import type {
  ActionPayload,
  AskUserForInfoFieldSpec,
  AskUserForInfoPayload,
} from '../../../lib/proposals/core/types';
import { isAskUserForInfoPayload } from '../../../lib/proposals/core/types';
import type { ActionRendererProps } from '../../../lib/proposals/registry';
import {
  ActionError,
  type LiveActionContent,
  ProposalContainer,
  ProposalInfoCallout,
  useAutoSaveCoordinationOptional,
  useAutoSaveFreezeOwner,
} from '../shared';
import VersionHistorySidebar from '../VersionHistorySidebar';
import { CannotAnswerExit } from './CannotAnswerExit';

// =============================================================================
// Module-scope constants
// =============================================================================

// Intentionally separate from the identically-valued constant in
// useClarifyingAnswersAutoSave.ts — each system can be tuned independently.
const AUTOSAVE_DEBOUNCE_MS = 2000;

/** No-op onChange for read-only PlateNoteEditor instances. */
const READONLY_ON_CHANGE = (_html: string) => undefined;

/** Human-readable labels for each save status (null = render nothing). */
const SAVE_STATUS_LABEL: Partial<Record<SaveStatus, string>> = {
  saving: 'Saving…',
  saved: 'Saved',
  error: "We couldn't save",
};

/**
 * Extract the visible text of an HTML fragment, used only to decide whether a
 * field is effectively empty (tags/whitespace only) so it can be stored as `''`.
 *
 * Parses via DOMParser into an inert document and reads `textContent` rather
 * than regex-stripping tags. A regex like `/<[^>]*>/g` is incomplete multi-
 * character sanitization (CodeQL) — it mishandles malformed/unterminated tags
 * and entity-encoded angle brackets — whereas DOMParser yields the true text
 * without executing scripts or fetching resources. Mirrors the DOMParser
 * approach in PlateNoteEditorImpl.tsx.
 */
function htmlToPlainText(html: string): string {
  return (
    new DOMParser().parseFromString(html, 'text/html').body.textContent ?? ''
  );
}

/**
 * True when an HTML fragment has no visible text — treats editor-emitted
 * empty variants ('<p></p>', '<p><br></p>', whitespace-only, …) as
 * equivalent regardless of their exact markup.
 */
function isEmptyHtml(html: string): boolean {
  return htmlToPlainText(html).trim() === '';
}

/**
 * Normalizes every answer to '' when empty-equivalent. Applied only at the
 * WIRE boundary (the outgoing save payload, and the save-echo comparison in
 * the reset effect) — the `answers` state itself keeps the raw HTML the
 * editor emitted so the `value` fed back down to PlateNoteEditor never
 * diverges from what PlateNoteEditorImpl last recorded as its own emitted
 * value. A divergence there (e.g. '<p></p>' vs '') makes the editor's sync
 * effect think the value changed externally and call `editor.tf.setValue`,
 * which resets the document AND the selection mid-typing.
 */
function normalizeAnswersForWire(
  answersMap: Record<string, string>
): Record<string, string> {
  const normalized: Record<string, string> = {};
  for (const key of Object.keys(answersMap)) {
    const html = answersMap[key] ?? '';
    normalized[key] = isEmptyHtml(html) ? '' : html;
  }
  return normalized;
}

/**
 * Serialize an answers map for dirty-tracking, independent of key order.
 *
 * `JSON.stringify` preserves key-insertion order, but the field-remap path
 * rebuilds the answers map in the new field order (see the reset effect), so
 * a raw stringify would report a change whenever the insertion order differs
 * even though every answer is identical. That false positive makes a flush
 * (clarifying submit / Approve) or the debounced autosave re-POST the same
 * payload and churn the action version. Sorting the keys first makes the
 * comparison depend only on content, not order.
 */
function serializeAnswers(answersMap: Record<string, string>): string {
  const ordered: Record<string, string> = {};
  for (const key of Object.keys(answersMap).sort()) {
    ordered[key] = answersMap[key] ?? '';
  }
  return JSON.stringify(ordered);
}

/** Restrict an answers map to the given field keys — the backend rejects any
 * answer key that is not a defined field. */
function restrictToFieldKeys(
  answersMap: Record<string, string>,
  fields: AskUserForInfoFieldSpec[]
): Record<string, string> {
  const allowed = new Set(fields.map(f => f.key));
  const out: Record<string, string> = {};
  for (const key of Object.keys(answersMap)) {
    if (allowed.has(key)) {
      out[key] = answersMap[key];
    }
  }
  return out;
}

// =============================================================================
// Types
// =============================================================================

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';
type CoordSaveState = 'idle' | 'pending' | 'saving' | 'error';

interface CoordinationHandle {
  setSaveState: (state: CoordSaveState) => void;
  setFlushFn: (fn: () => Promise<void>) => void;
  setContentFn: (fn: () => LiveActionContent | null) => void;
  unregister: () => void;
}

// =============================================================================
// AskUserForInfoFieldEditor — module-scope, not inline
// =============================================================================

interface AskUserForInfoFieldEditorProps {
  field: AskUserForInfoFieldSpec;
  value: string;
  readOnly: boolean;
  onChange: (key: string, html: string) => void;
  /**
   * Form-level save-status text. The whole payload saves in one request, so
   * this is rendered ONLY on the first field's header (passed as null for the
   * rest) — a single indicator, no per-field duplication, no extra row.
   */
  saveStatusText?: string | null;
  autoFocus?: boolean;
  /**
   * Id of the blocked-reason hint paragraph, threaded in only for the first
   * field while the hint is shown, so assistive tech announces the blocking
   * reason as part of this field's accessible description.
   */
  hintId?: string;
}

function AskUserForInfoFieldEditor({
  field,
  value,
  readOnly,
  onChange,
  saveStatusText = null,
  autoFocus = false,
  hintId,
}: AskUserForInfoFieldEditorProps) {
  const descriptionId = `field-desc-${field.key}`;
  // hintId comes first — the blocking reason should be announced before the
  // field's own description, per the multi-id aria-describedby convention.
  const ariaDescribedBy =
    [hintId, field.description ? descriptionId : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  // Stable per-key change handler — no inline arrow in JSX
  const handleChange = useCallback(
    (html: string) => onChange(field.key, html),
    [onChange, field.key]
  );

  return (
    <div className='space-y-1.5'>
      <div className='flex items-center justify-between gap-2 px-3'>
        <p className='text-sm font-medium text-fg-secondary'>{field.label}</p>
        {saveStatusText && (
          <span
            aria-hidden='true'
            className='shrink-0 text-xs text-fg-tertiary'
          >
            {saveStatusText}
          </span>
        )}
      </div>

      {field.description ? (
        <p className='px-3 text-xs text-fg-tertiary' id={descriptionId}>
          {field.description}
        </p>
      ) : null}

      {/* Light top border, square corners, no fill — the editor floats.
          Toolbar hidden: rich text still works via shortcuts/paste.

          minHeight is `auto`, not `compact`: Slate sets an inline
          `min-height: 24px` on the editable while it is empty and drops that
          inline style on the first keystroke. An inline style beats a class,
          so a floored option is suppressed until you type and then snaps in —
          measured, the box jumped 40px -> 64px on a single character. `auto`
          declares no floor, so the field starts at one line and grows a line
          at a time as the answer expands. */}
      <div className='border-t border-border-secondary'>
        <PlateNoteEditor
          ariaLabel={field.label}
          ariaDescribedBy={ariaDescribedBy}
          autoFocus={autoFocus}
          mentionsEnabled
          minHeight='auto'
          onChange={handleChange}
          placeholder='Your answer'
          readOnly={readOnly}
          richTextEnabled
          showToolbar={false}
          value={value}
        />
      </div>
    </div>
  );
}

// =============================================================================
// AskUserForInfoRenderer
// =============================================================================

export function AskUserForInfoRenderer({
  action,
  mode,
  className,
  onUpdate,
  proposalId,
  orgId,
  isProcessing,
  isOnlyAction,
}: ActionRendererProps) {
  // Derive payload BEFORE hooks — do not early-return yet (hooks must come first)
  const payload = isAskUserForInfoPayload(action.payload)
    ? (action.payload as AskUserForInfoPayload)
    : null;

  const effectiveEditable = mode === 'full' && !!onUpdate && !isProcessing;

  // ── State ────────────────────────────────────────────────────────────────
  const [answers, setAnswers] = useState<Record<string, string>>(() => ({
    ...(payload?.answers ?? {}),
  }));
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [restorableAnswers, setRestorableAnswers] = useState<
    Record<string, string>
  >({});
  const [showHistory, setShowHistory] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────────────
  const actionIdRef = useRef(action.id);
  const lastActionIdRef = useRef(action.id);
  const lastVersionRef = useRef(action.version_number);
  const lastFieldKeysRef = useRef<string[]>(
    payload?.fields.map(f => f.key) ?? []
  );
  const lastSavedRef = useRef(serializeAnswers(payload?.answers ?? {}));
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Retry timer for the exit-abort "persist once the shared freeze lifts" poll.
  // Kept SEPARATE from debounceTimerRef: an edit made while a footer still owns
  // the freeze re-runs the [answers, effectiveEditable] debounce effect, whose
  // cleanup clears debounceTimerRef — but that effect then returns early while
  // frozen without arming a replacement, so a shared timer would be silently
  // cancelled and the dirty edit would never persist when the footer unfreezes.
  const exitAbortRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const payloadRef = useRef(payload);
  payloadRef.current = payload;
  // Mirrored for imperative (non-render) reads by the restore-banner control
  // this state feeds — same pattern as the other *Ref mirrors above.
  const restorableAnswersRef = useRef(restorableAnswers);
  restorableAnswersRef.current = restorableAnswers;
  const executionOrderRef = useRef(action.execution_order);
  executionOrderRef.current = action.execution_order;
  const effectiveEditableRef = useRef(effectiveEditable);
  effectiveEditableRef.current = effectiveEditable;
  const isFirstRenderRef = useRef(true);
  const coordinationHandleRef = useRef<CoordinationHandle | null>(null);
  // Latest saveAnswers, so the flush bound at registration always invokes the
  // current save logic without forcing the handle to re-register whenever
  // saveAnswers' identity changes.
  const saveAnswersRef = useRef<() => Promise<void>>(() => Promise.resolve());
  // True while THIS renderer holds an autosave freeze it armed for an "I don't
  // have this information" exit and has not yet released. Used to release only
  // a freeze we own — never one another decision now holds.
  const exitFreezeArmedRef = useRef(false);
  // The freeze-owner token minted for the CURRENT exit decision, captured at
  // handleExitBegin. Every release targets exactly this token rather than the
  // hook's shared `currentToken` (which reflects the most recently minted freeze
  // across all callers): a release must lift the freeze THIS decision armed and
  // never a newer one. This is the per-decision-ownership pattern StickyFooter
  // uses — here `isDeciding` in CannotAnswerExit already serializes this
  // renderer's own decisions, so capturing the token keeps that guarantee
  // explicit and local rather than leaning on the shared latest-token ref.
  const exitFreezeTokenRef = useRef(0);
  // Stable identity of the LOGICAL action this renderer is showing:
  // `${proposalId}:${parent_action ?? id}`. `parent_action` is the root of the
  // version chain (the backend enforces it points to the root, never a child —
  // apps/actions/models.py), so `parent_action ?? id` is constant across every
  // autosave version bump and is exactly how the backend itself identifies a
  // logical action. It flips only on a genuine move to a different action.
  const logicalActionKey = `${proposalId}:${action.parent_action ?? action.id}`;
  // Tracks the logical-action key the exit-freeze navigation guard last saw, so
  // it can tell a genuine move to a different action from an autosave rollover.
  // Keying on action.id or execution_order is wrong: action.id changes on every
  // autosave (a new version is minted) and the backend can renumber
  // execution_order on rollover, so either would mistake that rollover for
  // navigation and lift the freeze mid-exit.
  const freezeNavKeyRef = useRef(logicalActionKey);
  // Failure from the most recent completed save attempt. saveAnswers never
  // rejects (its failure is swallowed into the 'error' status), so the
  // coordination flush reads this ref and rethrows it — a failed save must
  // BLOCK approval via flushAllAndWait rather than let the proposal resolve
  // while the coordination state is latched 'error' (which would arm the
  // unsaved-changes navigation guard on an already-approved proposal).
  // Cleared on success, when a suppressed/superseded save settles, and when
  // the reset effect discards the failed edit for incoming server state. On an
  // action-id rollover that keeps the edit, the registration effect re-seeds
  // the fresh handle 'error' from this ref so the flush is not skipped.
  const lastSaveErrorRef = useRef<Error | null>(null);
  // Set by the reset effect when an action-id rollover is the echo of our own
  // save AND the user kept editing after the snapshot we persisted (so the
  // reset effect keeps the still-live, newer edit). The registration effect
  // mints a fresh handle that starts 'idle'; without this signal the newer
  // unsaved edit would read 'idle' on the aggregate, StickyFooter would skip
  // flushAllAndWait, and approval could proceed before that edit is persisted.
  // The reset effect runs BEFORE the registration effect, so recording the
  // decision here (rather than re-deriving it in the registration effect from
  // answersRef — which is stale during a genuine reset's deferred setAnswers)
  // keeps the two in agreement. Distinct from lastSaveErrorRef, which re-seeds
  // 'error'; that takes precedence over this 'pending' re-seed.
  const reseedPendingAfterRolloverRef = useRef(false);
  // Barrier for the save currently in flight or queued. Saves are serialized
  // behind it so the approval flush never issues an update_payload write
  // concurrently with the debounced one: every save mints a new action
  // id/version, so an overlapping duplicate targets a superseded id.
  // Serializing also lets the queued write read the rolled-over
  // actionIdRef. Mirrors useCoordinatedAutoSave's inFlightRef.
  // TODO(#13784): delete this hand-rolled machinery when this editor
  // migrates onto useCoordinatedAutoSave, which provides it by construction.
  const saveInFlightRef = useRef<Promise<void> | null>(null);

  // ── Coordination ─────────────────────────────────────────────────────────
  const coordination = useAutoSaveCoordinationOptional();
  // Owns the freeze-owner token this renderer receives when it arms the exit
  // freeze. release(token) releases ONLY while we are still the owner — a
  // footer approve/reject (or any other decision) that re-froze the shared,
  // provider-wide freeze since bumps the owner past this token, and our
  // release then correctly no-ops instead of clearing a freeze that decision
  // still needs held. exitFreezeArmedRef (above) stays a plain ref: it is
  // this renderer's own "am I still armed" guard around each release call,
  // not something the hook owns.
  const exitFreeze = useAutoSaveFreezeOwner(coordination);

  // ── Keep actionIdRef in sync ─────────────────────────────────────────────
  useEffect(() => {
    actionIdRef.current = action.id;
  }, [action.id]);

  // ── Reset answers on action id / version change ──────────────────────────
  // Standard auto-save-editor reset: same id/version-ref pattern as
  // EmailActionPreview (originally shared with the since-deleted
  // PropertyUpdatePreview).
  useEffect(() => {
    if (
      action.id !== lastActionIdRef.current ||
      action.version_number !== lastVersionRef.current
    ) {
      lastActionIdRef.current = action.id;
      lastVersionRef.current = action.version_number;
      const freshPayload = isAskUserForInfoPayload(action.payload)
        ? (action.payload as AskUserForInfoPayload)
        : null;
      const freshAnswers = freshPayload?.answers ?? {};

      // saveAnswers mints a new action id/version on every request, so most
      // id/version rollovers are the echo of what THIS component just
      // saved, not a genuine external change. Compare normalized (empty-
      // equivalent) forms — the local raw HTML for a blanked field (e.g.
      // '<p></p>') differs textually from the server's normalized '' even
      // though it's the same save. Only reset local state — which would
      // clobber edits made since the save, including ones still in
      // progress — when the incoming payload genuinely differs (a
      // concurrent edit from another tab, or a server-side transform).
      const lastSavedAnswers = JSON.parse(lastSavedRef.current) as Record<
        string,
        string
      >;
      const prevKeys = lastFieldKeysRef.current;
      const newKeys = (freshPayload?.fields ?? []).map(f => f.key);
      const newKeySet = new Set(newKeys);
      const fieldsChanged =
        prevKeys.length !== newKeys.length ||
        prevKeys.some(k => !newKeySet.has(k));
      // A field-list change is NEVER a pure echo of our own save — saveAnswers
      // resends the same fields, so a differing field set means a genuine
      // external change (an AI rewrite that only reshaped the fields). Fold
      // that into the echo check so the remap branch runs and lastFieldKeysRef
      // is refreshed. Comparing answers alone would let a fields-only change
      // (answers byte-equal) short-circuit as an echo, leaving lastFieldKeysRef
      // stale so a LATER rewrite mis-remaps a removed, user-filled field and
      // discards its answer without offering restore.
      const isSaveEcho =
        !fieldsChanged &&
        serializeAnswers(normalizeAnswersForWire(freshAnswers)) ===
          serializeAnswers(normalizeAnswersForWire(lastSavedAnswers));

      if (!isSaveEcho) {
        const newIsAiRewrite =
          (action.version_number ?? 0) > 1 &&
          !!action.version_notes?.startsWith('AI update:');

        if (fieldsChanged) {
          // Remap: carry forward the user's local answer for every key that
          // survived; fall back to the incoming value for brand-new keys.
          // Keys the new field set dropped are intentionally left out.
          const localAnswers = answersRef.current;
          const merged: Record<string, string> = {};
          for (const key of newKeys) {
            // A key already present locally keeps its local value — even when
            // that value is empty HTML because the user just cleared the field.
            // Falling back to freshAnswers on emptiness would resurrect the
            // old answer and silently undo the clear. Only a brand-new key
            // (absent from the local answers map) takes the incoming value.
            merged[key] = Object.prototype.hasOwnProperty.call(
              localAnswers,
              key
            )
              ? (localAnswers[key] ?? '')
              : (freshAnswers[key] ?? '');
          }
          setAnswers(merged);
          // Lost = non-blank local answers whose key the new field set
          // dropped. Only offered for restore when the new version is an AI
          // rewrite, to stay coherent with the AI-rewrite banner.
          if (newIsAiRewrite) {
            const lost: Record<string, string> = {};
            for (const key of prevKeys) {
              if (
                !newKeySet.has(key) &&
                !isEmptyHtml(localAnswers[key] ?? '')
              ) {
                lost[key] = localAnswers[key];
              }
            }
            setRestorableAnswers(lost);
          } else {
            setRestorableAnswers({});
          }
        } else {
          // Fields unchanged → a foreign/concurrent edit or a different
          // action: replace local answers so the external change is
          // reflected (unchanged behavior).
          setAnswers({ ...freshAnswers });
          setRestorableAnswers({});
        }
        lastSavedRef.current = serializeAnswers(freshAnswers);
        lastFieldKeysRef.current = newKeys;
        // The local edits that produced a stale save failure are being
        // discarded in favor of the incoming server state, so the failure is
        // moot. Settle everything back to idle: clearing lastSaveErrorRef
        // alone would stop re-registration from re-seeding the fresh handle
        // 'error', but the editor would keep showing "We couldn't save"
        // (saveStatus) and the coordination aggregate would stay armed
        // 'error' for an edit that no longer exists. Applies to both the
        // remap and the fields-unchanged branch above — both adopt a
        // genuine external change.
        lastSaveErrorRef.current = null;
        setSaveStatus('idle');
        coordinationHandleRef.current?.setSaveState('idle');
        // Genuine external change adopted — nothing local left pending.
        reseedPendingAfterRolloverRef.current = false;
      } else {
        // Echo of our own save: the reset above is skipped so the still-live
        // edit is kept. If the user revised the answer after the snapshot we
        // persisted, that revision is unsaved and the fresh handle the
        // registration effect mints must reflect it as 'pending'. answersRef is
        // accurate here (the echo branch calls no setAnswers, so nothing defers
        // it); compare it via serializeAnswers against lastSavedRef (which is
        // always stored in sorted-key form), mirroring the pending decision
        // performSaveOrSettle makes when a save completes under a newer
        // in-flight edit. A live error re-seeds 'error' instead, so skip then.
        reseedPendingAfterRolloverRef.current =
          !lastSaveErrorRef.current &&
          serializeAnswers(answersRef.current) !== lastSavedRef.current;
      }
    }
  }, [action.id, action.version_number, action.version_notes, action.payload]);

  // ── Build updated payload ────────────────────────────────────────────────
  const buildUpdatedPayload = useCallback(
    (a: Record<string, string>): AskUserForInfoPayload => ({
      target_entity_id: payloadRef.current?.target_entity_id ?? '',
      fields: payloadRef.current?.fields ?? [],
      answers: restrictToFieldKeys(
        normalizeAnswersForWire(a),
        payloadRef.current?.fields ?? []
      ),
    }),
    []
  );

  // ── Save answers to backend ──────────────────────────────────────────────
  // The actual write. Runs strictly one-at-a-time via saveAnswers' queue
  // below; every suppressed path settles the coordination state so it can
  // never be left stuck 'pending'/'error' with nothing left to save.
  const performSaveOrSettle = useCallback(async () => {
    // Settle a suppressed/superseded save back to a quiescent state.
    const settleSuppressed = () => {
      lastSaveErrorRef.current = null;
      setSaveStatus(prev => (prev === 'saved' ? prev : 'idle'));
      coordinationHandleRef.current?.setSaveState('idle');
    };

    // Freeze check BEFORE the editable check: a timer or queued write
    // reaching here after approval started must not fire, and must settle
    // even when the resolving proposal has also flipped editable off —
    // returning through the editable check first would leave the latched
    // state armed forever.
    if (coordination?.isAutoSaveFrozen()) {
      settleSuppressed();
      return;
    }
    if (!orgId || !proposalId || !effectiveEditableRef.current) {
      return;
    }
    if (!payloadRef.current) {
      return;
    }
    // The write we queued behind may have just persisted exactly these
    // answers (the approval flush racing an in-flight debounced save).
    // Nothing left to write — settle instead of minting a duplicate action
    // version with a no-op payload. serializeAnswers (sorted-key) matches
    // lastSavedRef's stored form, so a remap that only reorders keys isn't
    // mistaken for a real change.
    if (serializeAnswers(answersRef.current) === lastSavedRef.current) {
      settleSuppressed();
      return;
    }

    setSaveStatus('saving');
    coordinationHandleRef.current?.setSaveState('saving');

    // Snapshot answers BEFORE awaiting. The payload we send and the value we
    // record in lastSavedRef must be the exact same snapshot — reading
    // answersRef.current again after the await would capture edits the user
    // made while the request was in flight and mark them as saved without ever
    // persisting them, dropping those edits on refresh/navigation.
    const savedAnswers = answersRef.current;
    try {
      const updatedPayload = buildUpdatedPayload(savedAnswers);
      const updatedAction = await createProposalClient(
        orgId
      ).updateActionPayload(proposalId, actionIdRef.current, {
        payload: updatedPayload as unknown as ActionPayload,
      });
      actionIdRef.current = updatedAction.id;
      lastSavedRef.current = serializeAnswers(savedAnswers);
      lastSaveErrorRef.current = null;
      onUpdate?.(updatedAction);
      // Did the user keep editing while this save was in flight? Compare the
      // live answers against the snapshot we actually sent. Settling to
      // 'saved'/idle would disarm the unsaved-changes guard for the rest of
      // the newer edit's debounce window — navigating away right then would
      // silently drop it. Stay pending instead: the armed debounce timer (or
      // the approval flush) persists the newer edit. Mirrors
      // EmailActionPreview's hasInflightEdits handling.
      if (serializeAnswers(answersRef.current) !== lastSavedRef.current) {
        setSaveStatus('saving');
        coordinationHandleRef.current?.setSaveState('pending');
      } else {
        setSaveStatus('saved');
        coordinationHandleRef.current?.setSaveState('idle');
      }
    } catch (error) {
      // Approval froze autosave while this write was in flight: the approve
      // POST carries the live answers (collectAllContent), so this failure
      // is superseded (typically UPDATE_FAILED because the proposal already
      // left `pending`). Settle instead of latching 'error'. The
      // pre-approval flush runs BEFORE the freeze, so a genuine flush
      // failure still latches below and blocks approval.
      if (coordination?.isAutoSaveFrozen()) {
        // Keep the superseded failure visible — EmailActionPreview logs every
        // autosave failure including the frozen path, and a silent settle
        // here would make this the hard editor to diagnose if the
        // update_payload conflicts recur.
        logger.warn(
          'ask_user_for_info save failed after approval froze autosave — settling',
          {
            proposalId,
            actionId: actionIdRef.current,
            error: error instanceof Error ? error.message : String(error),
          }
        );
        settleSuppressed();
        return;
      }
      // Carried out to the coordination flush, which rethrows it so
      // flushAllAndWait can block approval — see the registration effect.
      lastSaveErrorRef.current =
        error instanceof Error ? error : new Error(String(error));
      setSaveStatus('error');
      coordinationHandleRef.current?.setSaveState('error');
      logger.error(
        'Failed to save ask_user_for_info answers',
        error instanceof Error ? error : undefined,
        { proposalId, actionId: actionIdRef.current }
      );
    }
  }, [orgId, proposalId, onUpdate, coordination, buildUpdatedPayload]);

  // Public entry point: serializes the actual write behind any save already
  // in flight so no two callers — debounce timer, approval flush — ever
  // issue overlapping update_payload writes. Never rejects; failures
  // surface through lastSaveErrorRef.
  const saveAnswers = useCallback((): Promise<void> => {
    const prior = saveInFlightRef.current ?? Promise.resolve();
    const done = prior.then(() => performSaveOrSettle());
    // Non-rejecting barrier so one failed write can't poison the queue for
    // the next caller (performSaveOrSettle swallows its own failures, but guard
    // against pathological throws anyway).
    const barrier = done.then(
      () => undefined,
      () => undefined
    );
    saveInFlightRef.current = barrier;
    void barrier.then(() => {
      if (saveInFlightRef.current === barrier) {
        saveInFlightRef.current = null;
      }
    });
    return barrier;
  }, [performSaveOrSettle]);
  saveAnswersRef.current = saveAnswers;

  // ── Register with coordination + (re)bind flush/content per action id ─────
  // register() always starts a fresh registration with null flushFn/getContentFn,
  // so the new handle minted after an action-id rollover MUST have both callbacks
  // bound again here — otherwise Approve's flushAllAndWait/collectAllContent skip
  // this action and pending edits are lost. Binding inside the registration effect
  // (rather than separate saveAnswers/buildUpdatedPayload effects) guarantees the
  // current handle always carries both callbacks. saveAnswersRef keeps flush
  // pointed at the latest save logic without re-registering on every change.
  useEffect(() => {
    if (!coordination) {
      return;
    }
    const handle = coordination.register(`request-info-${action.id}`);
    coordinationHandleRef.current = handle;

    // register() always starts the fresh handle 'idle'. If a save failed and
    // the action id then rolled over while its edit is still unsaved (the
    // reset effect above kept the edit because the incoming payload was a save
    // echo), the failure is still live but the aggregate would read 'idle' —
    // StickyFooter would skip flushAllAndWait and approve around the unsaved
    // edit. Re-seed the handle 'error' so the flush is forced; its rethrow
    // then blocks approval (or a now-succeeding retry clears the error).
    //
    // The same rollover can also keep a newer edit that merely hasn't saved yet
    // (a save completed under an in-flight edit, minting the new action id): no
    // error, but the fresh 'idle' handle would still drop the pending state and
    // let approval skip the flush. The reset effect flags that case; re-seed
    // 'pending' so the newer edit is flushed before approval proceeds.
    if (lastSaveErrorRef.current) {
      handle.setSaveState('error');
    } else if (reseedPendingAfterRolloverRef.current) {
      handle.setSaveState('pending');
    }
    reseedPendingAfterRolloverRef.current = false;

    handle.setFlushFn(async () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      // The flush rail (clarifying submit, Approve) invokes every registered
      // editor unconditionally. Skip the save when nothing has changed since
      // the last one — an unconditional POST here re-saves identical answers
      // and churns the action version. Mirrors the debounced autosave's own
      // dirty guard.
      if (serializeAnswers(answersRef.current) === lastSavedRef.current) {
        return;
      }
      await saveAnswersRef.current();
      // saveAnswers never rejects — surface a failed save here so
      // flushAllAndWait reports it and StickyFooter BLOCKS approval,
      // instead of approving with the coordination state latched 'error'.
      if (lastSaveErrorRef.current) {
        throw lastSaveErrorRef.current;
      }
    });

    handle.setContentFn(() => ({
      actionId: actionIdRef.current,
      executionOrder: executionOrderRef.current,
      payload: buildUpdatedPayload(answersRef.current) as unknown as Record<
        string,
        unknown
      >,
    }));

    return () => {
      handle.unregister();
      coordinationHandleRef.current = null;
    };
  }, [coordination, action.id, buildUpdatedPayload]);

  // ── Debounced autosave on answers change ─────────────────────────────────
  // Standard auto-save-editor debounce: same first-render/frozen/editable
  // gating as EmailActionPreview (originally shared with the since-deleted
  // PropertyUpdatePreview).
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (!effectiveEditable) {
      return;
    }

    if (coordination?.isAutoSaveFrozen()) {
      return;
    }

    const currentStr = serializeAnswers(answers);
    if (currentStr === lastSavedRef.current) {
      return;
    }

    // Reflect unsaved edits immediately — otherwise the label stays "Saved"
    // for the whole debounce window while the user is actively typing.
    setSaveStatus('saving');
    coordinationHandleRef.current?.setSaveState('pending');

    debounceTimerRef.current = setTimeout(() => {
      void saveAnswersRef.current();
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, effectiveEditable]);

  // ── Escape exits the answer fields ───────────────────────────────────────
  // An otherwise-unclaimed Escape inside an answer editor blurs it, returning
  // keyboard context to the page (mention menus consume Escape first via
  // preventDefault — see useEscapeToBlur's layering contract).
  const fieldsContainerRef = useRef<HTMLDivElement | null>(null);
  const handleFieldsEscape = useEscapeToBlur(fieldsContainerRef);

  // ── "I don't have this information" exit callbacks ──────────────────────
  const cancelPendingAutosave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  // Cancel the self-re-arming exit-abort retry poll (persistWhenUnfrozen). It
  // lives in its OWN ref, so cancelPendingAutosave (which only clears the
  // autosave debounce) does not cover it. Every new exit/reassign decision, and
  // a successful reassign, must clear it: a poll left running from a PRIOR abort
  // (while a footer still owned the shared freeze) would otherwise fire
  // saveAnswers after the freeze finally lifts — issuing update_payload against
  // a proposal that may have since been handed off by a reassign (stale write /
  // 403 for the previous assignee).
  const cancelExitAbortRetry = useCallback(() => {
    if (exitAbortRetryTimerRef.current) {
      clearTimeout(exitAbortRetryTimerRef.current);
      exitAbortRetryTimerRef.current = null;
    }
  }, []);

  const handleExitBegin = useCallback(() => {
    cancelPendingAutosave();
    // A new exit decision begins: cancel any retry poll a prior abort left
    // running so it cannot fire update_payload during/after this decision.
    cancelExitAbortRetry();
    exitFreezeArmedRef.current = true;
    // Mint the owner token and capture it for THIS decision so the matching
    // abort / reassign-success / navigation / unmount release targets exactly
    // this freeze, never one a later decision (a footer approve/reject that
    // re-froze since) minted.
    exitFreezeTokenRef.current = exitFreeze.freeze();
  }, [cancelExitAbortRetry, cancelPendingAutosave, exitFreeze]);

  const handleReassignBegin = useCallback(async () => {
    cancelPendingAutosave();
    // A reassign hands the proposal off. A retry poll left running from a prior
    // abort MUST NOT survive into the handoff — after the assignee changes it
    // would fire saveAnswers → update_payload as the previous assignee (403 /
    // stale write to the new owner). Cancel it before we flush and freeze.
    cancelExitAbortRetry();
    // Persist the current answers WHILE WE ARE STILL THE ASSIGNEE. A reassign
    // hands the proposal to a colleague; afterwards the backend rejects an
    // update_payload from the previous assignee (403 "Only assigned user can
    // update payloads"), so this is the last moment a pending edit can be saved
    // for the new assignee to pick up. Unlike the reject path (handleExitBegin),
    // which discards answers, the reassign path flushes them. Save BEFORE
    // freezing — saveAnswers no-ops while frozen — and only when there is an
    // unsaved edit (serializeAnswers matches lastSavedRef's sorted-key form).
    if (serializeAnswers(answersRef.current) !== lastSavedRef.current) {
      await saveAnswersRef.current();
      // saveAnswers never rejects — its failure surfaces through
      // lastSaveErrorRef. If the pre-reassign save failed, the pending edit did
      // NOT reach the server, and once reassigned the previous assignee can no
      // longer update_payload (403). Throw so CannotAnswerExit's doReassign
      // catch aborts the reassign (runs onExitAbort, surfaces the error) rather
      // than handing the proposal off with an unsaved answer. Mirrors the flush
      // rail's rethrow in the registration effect above.
      if (lastSaveErrorRef.current) {
        throw lastSaveErrorRef.current;
      }
      // The flush can ALSO no-op WITHOUT setting lastSaveErrorRef: if a footer
      // approve/reject currently owns the shared autosave freeze, saveAnswers
      // settles-while-frozen (performSaveOrSettle returns at the freeze check
      // and clears the error rail), so the edit never reached the server yet
      // lastSaveErrorRef stays null. We already cancelled the exit-abort retry
      // poll above, so nothing else is scheduled to persist it. If the answer is
      // still unsaved after the flush, do NOT hand the proposal off — that would
      // silently drop the dirty answer. Throw so doReassign aborts and runs
      // onExitAbort (handleExitAbort), which re-arms persistWhenUnfrozen to save
      // the answer once the shared freeze lifts; the reassign can be retried
      // after the in-progress footer decision finishes.
      if (serializeAnswers(answersRef.current) !== lastSavedRef.current) {
        throw new Error(
          'Answer could not be saved before reassignment because another ' +
            'decision on this proposal is still in progress.'
        );
      }
    }
    // Freeze now so a late sibling autosave cannot fire update_payload during
    // the resolving reassign. Mint and capture the owner token exactly as
    // handleExitBegin does, so the matching release targets this freeze.
    exitFreezeArmedRef.current = true;
    exitFreezeTokenRef.current = exitFreeze.freeze();
  }, [cancelExitAbortRetry, cancelPendingAutosave, exitFreeze]);

  const handleExitAbort = useCallback(() => {
    exitFreezeArmedRef.current = false;
    // Release THIS decision's own token. If a footer decision re-froze the
    // shared provider freeze since handleExitBegin, that decision holds a newer
    // token; releasing ours removes only our hold, so the reference-counted
    // freeze stays held for it (a late debounce firing update_payload during a
    // resolving decision would fail with UPDATE_FAILED).
    exitFreeze.release(exitFreezeTokenRef.current);
    // The exit failed, so the proposal is still pending. The begin handler
    // cancelled the pending autosave debounce before the decision; unfreezing
    // only flips a ref, so a recent edit would otherwise sit unsaved with no
    // timer armed. Persist it now — but a footer approve/reject may STILL own
    // the shared freeze (we released only our own token above), in which case
    // saveAnswers no-ops (performSaveOrSettle settles while frozen) and nothing
    // is scheduled to retry, so the dirty edit would be discarded on navigation.
    // Poll until the shared freeze lifts, then save. Terminates on any of:
    // nothing dirty, the proposal resolving (no longer editable), or the freeze
    // lifting. Stored in its OWN timer ref (exitAbortRetryTimerRef), NOT
    // debounceTimerRef: while the footer still owns the freeze, an edit re-runs
    // the [answers, effectiveEditable] debounce effect whose cleanup clears
    // debounceTimerRef and then returns early (frozen) without arming a
    // replacement — a shared timer would be cancelled and the dirty edit would
    // never persist once the footer unfreezes. A dedicated ref survives that.
    // Compare via serializeAnswers so the dirty check matches lastSavedRef's
    // sorted-key form (a raw JSON.stringify would false-positive on key order).
    const persistWhenUnfrozen = () => {
      exitAbortRetryTimerRef.current = null;
      if (serializeAnswers(answersRef.current) === lastSavedRef.current) {
        return;
      }
      // Proposal resolved elsewhere (a footer approve/reject succeeded): the
      // renderer is read-only now and an update_payload would be rejected.
      if (!effectiveEditableRef.current) {
        return;
      }
      if (coordination?.isAutoSaveFrozen()) {
        exitAbortRetryTimerRef.current = setTimeout(
          persistWhenUnfrozen,
          AUTOSAVE_DEBOUNCE_MS
        );
        return;
      }
      void saveAnswersRef.current();
    };
    // Clear any poll left running from a prior abort before re-arming, so we
    // never leak an orphaned timer.
    cancelExitAbortRetry();
    persistWhenUnfrozen();
  }, [cancelExitAbortRetry, exitFreeze, coordination]);

  const handleReassignSuccess = useCallback(() => {
    // A SUCCESSFUL reassign leaves the proposal PENDING with a new assignee —
    // it does NOT resolve the proposal the way a reject does, and this renderer
    // can stay mounted and editable (the ProposalDetailPanel surface does not
    // auto-advance on reassign). The AutoSaveCoordinationProvider is mounted
    // once and reused, unkeyed, across proposals, so a freeze left armed here
    // would never be lifted (neither the logical-action nav guard nor the
    // unmount cleanup fires while the same proposal stays on screen) and would
    // permanently block autosave. Release our own hold now. Token-gated, so if a
    // footer decision re-froze since, releasing our token can't lift its freeze.
    //
    // The reject-success path deliberately has NO release: that proposal is
    // resolving, and a late sibling debounce firing against it must stay blocked
    // (UPDATE_FAILED). Clearing the armed flag also stops the nav/unmount
    // effects from redundantly releasing an already-released token.
    //
    // No save happens here: handleReassignBegin already flushed the pending
    // answers BEFORE the assignee changed. Saving now would 403 — the previous
    // assignee can no longer update_payload — so releasing the freeze is all
    // that remains.
    exitFreezeArmedRef.current = false;
    exitFreeze.release(exitFreezeTokenRef.current);
    // Belt-and-suspenders: after handoff, guarantee no retry poll left running
    // from a prior abort survives to fire update_payload as the (now former)
    // assignee. handleReassignBegin already cancelled it, but a successful
    // reassign is exactly the moment a lingering stale write would be worst.
    cancelExitAbortRetry();
  }, [cancelExitAbortRetry, exitFreeze]);

  // A SUCCESSFUL exit intentionally leaves the freeze armed (like StickyFooter's
  // approve/reject success path): the proposal is resolving, and a late debounce
  // from a sibling card firing against it would be rejected (UPDATE_FAILED). But
  // the AutoSaveCoordinationProvider is mounted once and reused, unkeyed, across
  // proposals, so a freeze left armed here must be lifted once this renderer
  // moves off the proposal it froze for — otherwise the NEXT proposal in the
  // reused surface can never persist edits.
  //
  // Key this on the LOGICAL action id (`proposalId` + `parent_action ?? id`),
  // NOT action.id or execution_order: saveAnswers mints a new action id/version
  // on every autosave, and the backend can renumber execution_order on that
  // rollover, so either would be misread as navigation. `parent_action ?? id`
  // is the version-chain root — constant across version bumps — so it flips only
  // on a genuine move to a different action, the one moment releasing the freeze
  // is safe. Release is additionally token-gated (release()), so even on a
  // real move we never clear a freeze another decision has since re-taken.
  useEffect(() => {
    if (logicalActionKey !== freezeNavKeyRef.current) {
      freezeNavKeyRef.current = logicalActionKey;
      if (exitFreezeArmedRef.current) {
        exitFreezeArmedRef.current = false;
        exitFreeze.release(exitFreezeTokenRef.current);
      }
    }
  }, [logicalActionKey, exitFreeze]);

  // Belt-and-suspenders for the case where this renderer unmounts on navigation
  // (the next proposal has no ask_user_for_info card) rather than being reused:
  // release a freeze we still own so the reused provider does not stay frozen.
  // Token-gated for the same reason as above.
  useEffect(() => {
    return () => {
      if (exitFreezeArmedRef.current) {
        exitFreezeArmedRef.current = false;
        exitFreeze.release(exitFreezeTokenRef.current);
      }
    };
  }, [exitFreeze]);

  // Clear the exit-abort retry poll on unmount — it lives in its own ref, so the
  // debounce effect's cleanup does not cover it.
  useEffect(() => {
    return () => {
      cancelExitAbortRetry();
    };
  }, [cancelExitAbortRetry]);

  // ── Field change handler ─────────────────────────────────────────────────
  const handleFieldChange = useCallback((key: string, html: string) => {
    setAnswers(prev => {
      const prevValue = prev[key] ?? '';
      // No-op when both the stored and incoming value are empty-equivalent
      // (e.g. the editor re-emits normalized-but-equivalent content such as
      // '<p></p>' for an already-empty field). Returning the same reference
      // keeps the form clean: no new answers object means no autosave
      // debounce, no coordination 'pending', and no unsaved-changes guard
      // arming on navigation.
      if (isEmptyHtml(prevValue) && isEmptyHtml(html)) {
        return prev;
      }
      if (prevValue === html) {
        return prev;
      }
      // Store the raw HTML as-is — even when empty-equivalent (e.g.
      // '<p></p>') — rather than normalizing to ''. This value round-trips
      // back down as the editor's `value` prop; normalizing it here would
      // make it diverge from what PlateNoteEditorImpl just recorded as its
      // own last-emitted value, triggering an unwanted document + selection
      // reset (see normalizeAnswersForWire). The wire payload is normalized
      // separately, in buildUpdatedPayload.
      return { ...prev, [key]: html };
    });
  }, []);

  // ── Restore a lost answer into a still-present field ─────────────────────
  const handleRestore = useCallback(() => {
    const fields = payloadRef.current?.fields ?? [];
    if (fields.length === 0) {
      return;
    }
    const firstKey = fields[0].key;
    const fieldKeys = new Set(fields.map(f => f.key));
    const { current: currentAnswers } = answersRef;
    // Group every lost answer by the field it lands on. Because restorable
    // answers are always dropped keys, they all fall back to the first field —
    // so with two or more lost answers a per-entry setAnswers would have each
    // overwrite the previous, leaving only the last. Concatenate the fragments
    // (after any text already in the target field) so nothing is lost.
    const fragmentsByTarget = new Map<string, string[]>();
    for (const [key, html] of Object.entries(restorableAnswers)) {
      const targetKey = fieldKeys.has(key) ? key : firstKey;
      const fragments = fragmentsByTarget.get(targetKey) ?? [];
      fragments.push(html);
      fragmentsByTarget.set(targetKey, fragments);
    }
    for (const [targetKey, fragments] of fragmentsByTarget) {
      const existing = currentAnswers[targetKey] ?? '';
      const parts = isEmptyHtml(existing)
        ? fragments
        : [existing, ...fragments];
      handleFieldChange(targetKey, parts.join(''));
    }
    setRestorableAnswers({});
  }, [restorableAnswers, handleFieldChange]);

  // ── Version-history sidebar open/close ────────────────────────────────────
  const handleOpenHistory = useCallback(() => {
    setShowHistory(true);
  }, []);
  const handleHistoryOpenChange = useCallback((open: boolean) => {
    if (!open) {
      setShowHistory(false);
    }
  }, []);

  // ── Early return AFTER all hooks ─────────────────────────────────────────
  if (!payload) {
    return (
      <ActionError message='Invalid ask_user_for_info payload' mode={mode} />
    );
  }

  // ── Readonly mode ─────────────────────────────────────────────────────────
  if (mode === 'readonly') {
    return (
      <ProposalContainer className={className}>
        <div className='space-y-4 pt-3'>
          {payload.fields.map(field => {
            const answer = answers[field.key] ?? '';
            return (
              <div className='space-y-1.5' key={field.key}>
                <p className='px-3 text-sm font-medium text-fg-secondary'>
                  {field.label}
                </p>
                {field.description ? (
                  <p className='px-3 text-xs text-fg-tertiary'>
                    {field.description}
                  </p>
                ) : null}
                {answer ? (
                  <div className='border-t border-border-secondary'>
                    <PlateNoteEditor
                      ariaLabel={field.label}
                      mentionsEnabled
                      minHeight='compact'
                      onChange={READONLY_ON_CHANGE}
                      readOnly
                      richTextEnabled
                      value={answer}
                    />
                  </div>
                ) : (
                  <p className='px-3 text-sm text-fg-tertiary'>
                    No answer provided.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </ProposalContainer>
    );
  }

  // ── Full mode ─────────────────────────────────────────────────────────────
  if (payload.fields.length === 0) {
    return (
      <ProposalContainer className={className}>
        <p className='text-sm text-fg-tertiary'>No fields to fill in.</p>
      </ProposalContainer>
    );
  }

  const saveStatusText = SAVE_STATUS_LABEL[saveStatus] ?? null;
  const isAiRewrite =
    (action.version_number ?? 0) > 1 &&
    !!action.version_notes?.startsWith('AI update:');
  const versions = action.versions ?? [];
  const hasHistory = versions.length > 1;
  const hasRestorable = Object.keys(restorableAnswers).length > 0;

  const submitBlockedReason = action.submit_blocked_reason?.trim()
    ? action.submit_blocked_reason
    : null;
  // Only consider answers for CURRENTLY-rendered fields. `answers` can retain
  // stale keys from a previous field set (an AI rewrite that changed fields
  // leaves the old keys behind); counting those would hide the blocked-submit
  // hint even though every visible editor is empty.
  const hasAnyLocalAnswer = payload.fields.some(
    field => !isEmptyHtml(answers[field.key] ?? '')
  );
  // The hint reflects THIS action's own blocked-submit reason, so it's shown
  // whenever this action is editable, blocked, and untouched — independent of
  // how many other actions the proposal has. (Auto-focus is the separate,
  // isOnlyAction-gated concern below: stealing page focus is only acceptable
  // when this is the only thing on the page.)
  const showBlockedHint =
    effectiveEditable && submitBlockedReason !== null && !hasAnyLocalAnswer;
  const blockedHintId = `blocked-reason-hint-${action.id}`;

  return (
    <ProposalContainer className={className}>
      {isAiRewrite && (
        <ProposalInfoCallout>
          <p className='text-sm'>
            Ahoy updated this form. {action.version_notes}
          </p>
          <div className='mt-1.5 flex items-center gap-3'>
            {hasHistory && (
              <Button color='link-color' onClick={handleOpenHistory} size='sm'>
                View history
              </Button>
            )}
            {hasRestorable && effectiveEditable && (
              <Button color='secondary' onClick={handleRestore} size='sm'>
                Restore answer
              </Button>
            )}
          </div>
        </ProposalInfoCallout>
      )}

      {/* a11y: announce save-status changes (sr-only is absolute — no layout impact). */}
      <div aria-atomic='true' aria-live='polite' className='sr-only'>
        {saveStatusText ?? ''}
      </div>

      {showBlockedHint ? (
        <p className='px-3 pt-3 text-sm text-fg-secondary' id={blockedHintId}>
          {submitBlockedReason}
        </p>
      ) : null}

      <div
        className='space-y-4 pt-3'
        onKeyDown={handleFieldsEscape}
        ref={fieldsContainerRef}
      >
        {payload.fields.map((field, index) => (
          <AskUserForInfoFieldEditor
            key={field.key}
            // Gated on isOnlyAction (unlike showBlockedHint above): stealing
            // page focus is only acceptable when this is the only thing on
            // the page, not a hint-visibility concern.
            autoFocus={!!isOnlyAction && effectiveEditable && index === 0}
            field={field}
            hintId={showBlockedHint && index === 0 ? blockedHintId : undefined}
            onChange={handleFieldChange}
            readOnly={!effectiveEditable}
            saveStatusText={index === 0 ? saveStatusText : null}
            value={answers[field.key] ?? ''}
          />
        ))}
      </div>

      {effectiveEditable &&
      orgId != null &&
      proposalId != null &&
      action.submit_blocked_reason?.trim() ? (
        // The exit sits in its own divided footer strip so it reads as the
        // card's escape hatch, not as a trailing line of the last field.
        <div className='mt-4 border-t border-border-secondary px-3 py-3'>
          <CannotAnswerExit
            orgId={orgId}
            proposalId={proposalId}
            onExitBegin={handleExitBegin}
            onReassignBegin={handleReassignBegin}
            onExitAbort={handleExitAbort}
            onReassignSuccess={handleReassignSuccess}
          />
        </div>
      ) : null}

      {isAiRewrite && (
        <VersionHistorySidebar
          currentVersionId={action.id}
          isOpen={showHistory}
          onOpenChange={handleHistoryOpenChange}
          versions={versions}
        />
      )}
    </ProposalContainer>
  );
}
